<template>
  <div class="ba-targeting-node"
      :class="[{
        'hovered': isHovered,
        'active': isActive,
        'forward': isForwardDirection,
        'backward': !isForwardDirection,
        'bookmarked': nodeInfo.bookmark,
        'same-as-current': nodeInfo.sameAsCurrent,
        'no-thumbnail': !nodeInfo.thumbnail,
        'display-group': grouping,
        'trackable': isTrackable
        },
        device
      ]"
      :style="nodeInlineStyles"
      :data-sequence-index="sequenceIndex"
      @mouseenter="onNodeMouseEnter"
      @mouseleave="onNodeMouseLeave"
      @click="onNodeClick">
    <i v-if="useNullity && nodeData.nullityCnt" class="icon icon--nullity" />
    <template v-if="!isCustomNode">
      <span class="thumbnail"
          :style="{
            'background-image': 'url(' + (nodeInfo.thumbnail || defaultThumbnail)
          }" />
      <heatmap-sample v-if="isShowHeatmap" :device="device"></heatmap-sample>
      <transition name="group-name">
      <span v-if="grouping"
            class="group-name"
            :class="{
              'untagged': groupName === $t('group.upcoming')
            }">
        <span>{{ groupName }}</span>
        <i class="icon icon--upcoming-large"></i>
      </span>
      </transition>
      <div class="buttons" v-if="isHovered">
        <div class="wrap">
          <button class="heatmap" :class="{'hovered': buttonHover}"
                  @click.stop="onHeatmap(nodeInfo, nodeData)"
                  @mouseenter.stop="buttonHover = true;"
                  @mouseleave.stop="buttonHover = false"
                  v-if="isShowHeatmap">
            <i class="icon icon--heatmap-on small"></i>
            <span class="label"> {{ $t('common.heatmap') }}</span>
          </button>
        </div>
      </div>

      <ba-node-link v-if="nodeInfo"
                    :pageData="nodeInfo"
                    :displayBackLetters="false"
                    :isCustomNode="isCustomNode"
                    @onMouseEnter="isHovered = false"
                    @onMouseLeave="isHovered = true" />
    </template>
    <template v-else>
      <span
            class="group-name"
            :class="{
              'untagged': nodeInfo.customId === 0
            }"
            :style="{'background-color': nodeInfo.customColor}"
      >
        <span>{{ nodeInfo.customId === 0 ? $t('customTag.untaggedLabel') : nodeInfo.customValue}}</span>
        <i class="icon icon--upcoming-large"></i>
      </span>
    </template>

    <ba-targeting-node-values :nodeData="nodeData"
                              :visibleOptions="visibleOptions"
                              :numSelectedVisibleOptions="numSelectedVisibleOptions"
                              :isForwardDirection="isForwardDirection" />
    <ba-targeting-dropoff-path v-if="nodeData._dropPathInfo"
                              :nodeData="nodeData" />
    <ba-tracking-bar
        v-if="isTrackable"
        :total="nodeData.sessCnt"
        :member="nodeData.memberSessCnt"
        :login="nodeData.loggedInSessCnt"
        :newLogin="nodeData.newlyLoggedInSessCnt"
        @onMouseEnter="isHovered = false"
        @onMouseLeave="isHovered = true"
        @onClick="$emit('onShowTrackingLayer', sequenceIndex, nodeIndex)"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { onlySubdomain, hexToRgb, utcToLocalTimezone } from '@/libs/utils'

import BaNodeLink from '@/components/trend/NodeLink'
import BaTargetingNodeValues from '@/components/targeting/NodeValues'
import BaTargetingDropoffPath from '@/components/targeting/NodeDropoffPath'
import { differenceInDays, format } from 'date-fns'
import { HEATMAP_STATE } from '@/libs/constants'
import HeatmapSample from '@/components/heatmap/HeatmapSample.vue'
import BaTrackingBar from '@/components/journey/TrackingBar.vue'

export default {
  name: 'BaTargetingNode',
  components: {
    HeatmapSample,
    BaNodeLink,
    BaTargetingNodeValues,
    BaTargetingDropoffPath,
    BaTrackingBar
  },
  props: {
    isForwardDirection: {
      type: Boolean,
      required: true
    },
    isActive: {
      type: Boolean,
      required: true
    },
    layout: {
      type: Object,
      required: true
    },
    nodeWidth: {
      type: Number,
      required: true
    },
    nodeHeight: {
      type: Number,
      required: true
    },
    nodeData: {
      type: Object,
      required: true
    },
    nodeInfo: {
      type: Object,
      required: true
    },
    visibleOptions: {
      type: Object,
      required: true
    },
    numSelectedVisibleOptions: {
      type: Number,
      required: true
    },
    numCroppedDisplayableSequences: {
      type: Number,
      required: true
    },
    sequenceIndex: {
      type: Number,
      required: true
    },
    nodeIndex: {
      type: Number,
      required: true
    },
    defaultThumbnail: {
      type: String
    },
    isTrackable: {
      default: false
    },
    grouping: {
      type: Boolean,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    useNullity: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isHovered: false,
      buttonHover: false,
      isShowHeatmap: false
    }
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'journeyPeriod',
      'journeyTargetingOptions',
      'domain'
    ]),
    isCustomNode () {
      return this.journeyOptions.isCustomNode
    },
    isHeatmapAvailableRange () {
      const start = this.journeyPeriod.start
      const end = this.journeyPeriod.end
      const startDate = new Date(start)
      const endDate = new Date(end)

      const diffDay = differenceInDays(endDate, startDate)
      return diffDay <= 31
    },
    device () {
      return this.journeyOptions.device || 'all'
    },
    hasGroup () {
      return this.nodeInfo && this.nodeInfo.tagInfo && this.nodeInfo.tagInfo[this.group]
    },
    groupName () {
      return this.hasGroup ? onlySubdomain(this.nodeInfo.tagInfo[this.group].tagName || this.$t('group.upcoming'), this.journeyOptions.domain) : this.$t('group.upcoming')
    },
    coverColor () {
      if (this.grouping) {
        const color = hexToRgb(this.hasGroup ? this.nodeInfo.tagInfo[this.group].colorHex || '#bbb' : '#bbb')
        return 'rgba(' + color.r + ',' + color.g + ',' + color.b + ', 0.8)'
      }
      return 'rgba(0, 0, 0, 0.15)'
    },
    nodeInlineStyles () {
      const styles = {
        'width': this.nodeWidth + 'px',
        'height': this.nodeHeight + 'px',
        'top': this.layout.y + 'px',
        'z-index': this.numCroppedDisplayableSequences - this.sequenceIndex,
        '--cover-color': this.coverColor
      }
      styles[this.isForwardDirection ? 'left' : 'right'] = this.layout.x + 'px'
      styles['margin-' + (this.isForwardDirection ? 'left' : 'right')] = -(this.nodeWidth / 2) + 'px'
      return styles
    }
  },
  created () {
    // console.log('node', this.nodeInfo, this.nodeData)
    this.onCheckHeatmap()
  },
  mounted () {
  },
  updated () {
  },
  beforeDestroy () {
  },
  methods: {
    onCheckHeatmap () {
      // console.log('onCheckHeatmap taget :', this.nodeInfo)
      if (!this.isHeatmapAvailableRange) {
        this.isShowHeatmap = false
        return false
      }

      if (!this.nodeInfo.baApply || !this.nodeInfo.heatmapAid) {
        this.isShowHeatmap = false
        return false
      }

      if (this.nodeInfo.heatmapUiState === HEATMAP_STATE.ANALYZING) {
        this.isShowHeatmap = false
        return false
      }

      const installDate = this.nodeInfo.baApply.installDate
      if (installDate) {
        const installDateStr = format(utcToLocalTimezone(installDate, this.domain.timezoneName), 'YYYY-MM-DD')

        if (this.journeyPeriod.end < installDateStr) {
          this.isShowHeatmap = false
          return false
        }
      } else {
        this.isShowHeatmap = false
        return false
      }

      if (this.nodeInfo.isExistHeatmap) {
        this.isShowHeatmap = true
      }
    },
    onHeatmap (pageData, nodeData) {
      // console.log('node, ', pageData, nodeData)
      pageData = {
        ...pageData,
        ...nodeData,
        fromHash: nodeData.from.from_hash,
        targetNodeId: this.journeyTargetingOptions.targetNodeId
      }
      this.$emit('onHeatmap', pageData)
    },
    onNodeMouseEnter () {
      this.isHovered = true
    },
    onNodeMouseLeave () {
      this.isHovered = false
    },
    onNodeClick (e) {
      this.$emit('onNodeClick', e, this.sequenceIndex, this.nodeIndex)
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-targeting-node {
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 15px;
  background: #fff;
  box-shadow: 0 15px 12px -8px rgba(0, 0, 0, 0.3);
  transition: top 0.45s $ease-out-expo;
  cursor: pointer;
  pointer-events: auto;
  z-index: 1;

  .buttons {
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: 99;
    opacity: 0;
    transition: opacity 0.2s ease;

    .wrap {
    }

    button {
      border-radius: 4px;
      white-space: nowrap;
      position: relative;
      z-index: 100;
      padding: 3px 3px 3px 3px;
      text-align: left;
      transition: background-color 0.2s ease;

      .icon {
        vertical-align: middle;
      }

      .label {
        position: relative;
        top: -1px;
        z-index: 100;
        cursor: pointer;
        color: #ffffff;
        overflow: hidden;
        display: inline-block;
        max-width: 0;
        transition: max-width 0.2s ease, margin 0.2s ease;
        vertical-align: middle;
        margin: 0;
      }

      &.hovered {
        .label {
          max-width: 100px;
          margin: 0 5px 0 4px;
        }
      }

      &.heatmap {
        margin-right: 4px;
        background-color: #272727;
      }
    }
  }

  &:hover {
    z-index: 99 !important;

    .buttons {
      opacity: 1;
    }
  }

  &.backward {
    left: auto;
    right: 0;
  }

  &.active,
  &.hovered {
    box-shadow: 0 15px 12px -8px rgba(0, 0, 0, 0.3);

    &:before {
      box-shadow: inset 0 0 0 3px #fff, 0 0 0 3px #0074ff;
    }
  }

  body.dragging & {
    pointer-events: none;
  }

  &:before {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    content: '';
    border-radius: 15px;
    background-color: var(--cover-color);
    box-shadow: inset 0 0 0 3px #fff, 0 0 0 1px rgba(0, 0, 0, 0.05);
    z-index: 1;
    transition: background-color 0.3s ease-out, box-shadow 0.2s ease-out;
  }

  &.bookmarked:after,
  &.same-as-current:after {
    position: absolute;
    left: 3px;
    top: 3px;
    width: calc(100% - 6px);
    height: calc(100% - 6px);
    text-indent: -999em;
    border-radius: 12px;
    overflow: hidden;
    z-index: 2;
    transition: opacity 0.1s;
  }
  &.bookmarked:after {
    content: 'Bookmarked';
    background: url('../../assets/img/targeting/icon--bookmark-ribbon.svg') no-repeat 0 0 / 40px;
  }
  &.same-as-current:after {
    content: 'Same as current';
    background: url('../../assets/img/targeting/icon-targeting-current.svg') no-repeat 100% 100% / auto;
    overflow: hidden;
  }
  &.bookmarked.same-as-current:after {
    content: 'Bookmarked / Same as current';
    background: url('../../assets/img/targeting/icon--bookmark-ribbon.svg') no-repeat 0 0 / 40px, url('../../assets/img/targeting/icon-targeting-current.svg') no-repeat 100% 100% / auto;
  }

  &.bookmarked.hovered.display-group {
    &:after {
      opacity: 0;
    }
  }

  &.no-thumbnail {
    .thumbnail:before {
      width: inherit;
      height: inherit;
      content: '';
      border-radius: inherit;
      background: inherit;
      display: block;
      transform: scale(1.175);
      filter: blur(5px);
    }
    .thumbnail:after {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      content: '';
      background: url('../../assets/img/journey/icon_add_image.svg') no-repeat 50% 50% / 24px;
    }
  }

  .thumbnail {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 15px;
    background: #fff no-repeat 0% 0% / cover;
    display: block;
    overflow: hidden;
  }

  &.trackable {
    /deep/ .tracking-bar {
      bottom: -5px;
      transform: translate(-50%, 100%) scale(0.6);
      transform-origin: center top;
      width: 180px;
      transition: all 0.4s ease;
      &:hover {
        transform: translate(-50%, 100%) scale(1);
      }
    }

    /deep/ .ba-targeting-values {
      top: calc(100% + 17px);
    }
  }

  &.tablet {
    .group-name {
      span {
        max-height: calc(1.23em * 3);
        line-height: 1.23;
        -webkit-line-clamp: 3;
      }

      &.untagged {
        padding-top: 31px;

        .icon {
          top: 50px;
        }
      }
    }

    .icon--nullity {
      left: -30px;
    }

    .ba-targeting-values {
      min-width: 70px;
    }

    .link-wrap {
      left: -5px;
      right: -5px;
    }
  }

  &.phone {
    .group-name {
      padding: 5px;

      span {
        max-height: calc(1.33em * 4);
        font-size: 12px;
        line-height: 1.33;
        -webkit-line-clamp: 4;
      }

      &.untagged {
        white-space: nowrap;
        padding-top: 31px;

        .icon {
          left: 5px;
          top: 51px;
        }
      }
    }

    .icon--nullity {
      left: -40px;
    }

    .ba-targeting-values {
      min-width: 52px;
    }

    .link-wrap {
      left: -15px;
      right: -15px;
    }
  }

  &.all {
    border-radius: 100%;
    &:before, &:after {
      border-radius: 100%;
    }
    &.bookmarked:after {
      background: url('../../assets/img/targeting/icon--bookmark-round.svg') no-repeat -2% -4% / 40px;
    }
    .thumbnail {
      border-radius: 100%;
    }
    .group-name {
      padding: 31px 12px 12px;

      span {
        height: fit-content;
        font-size: 14px;
        line-height: 1.2;
        -webkit-line-clamp: 2;
      }

      &.untagged {
        .icon {
          left: 12px;
          top: 51px;
        }
      }
    }

    .icon--nullity {
      left: -25px;
    }

    .ba-targeting-values {
      min-width: 84px;
      & /deep/ .item {
        &:first-child {
          margin-top: 8px;
        }
      }
    }

    .link-wrap {
      left: 0;
      right: 0;
    }

    .buttons {
      top: 16px;
      left: 20px;
    }
  }

  .group-name {
    position: absolute;
    left: 3px;
    right: 3px;
    top: 3px;
    bottom: 3px;
    padding: 9px;
    border-radius: 15px;
    z-index: 1;

    span {
      height: 100%;
      max-height: calc(1.31em * 2);
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      word-break: break-all;
      text-overflow: ellipsis;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
      line-height: 1.31;
      display: block;
      overflow: hidden;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      display: -webkit-box;
    }

    .icon {
      display: none;
    }

    &.untagged {
      .icon {
        position: absolute;
        left: 9px;
        top: 31px;
        display: block;
      }
    }

    &-enter-active, &-leave-active {
      transition: opacity 0.3s ease-out;
    }
    &-enter, &-leave-to {
      opacity: 0;
    }
  }

  .icon--nullity {
    position: absolute;
    left: -20px;
    top: -21px;
  }

  .link-wrap {
    left: 5px;
    right: 5px;
    top: -23px;
    color: #555;
    transform: none;
    z-index: 1;
  }
}
</style>
